import { useId } from "react";
import type { JSX } from "react";
import { activityLabel } from "../../domain/activities";
import type { ActivityOption } from "../../domain/activities";
import { useI18n } from "../../shared/i18n/I18nContext";
import styles from "../../shared/ui/ui.module.css";

/** Properties of {@link ActivityChoice}. */
export interface ActivityChoiceProps {
  /** The label of the field. */
  readonly label: string;
  /** The activities that can be chosen for the project. */
  readonly activities: readonly ActivityOption[];
  /** The chosen activity, or `null` for none. */
  readonly value: string | null;
  /** Whether the field cannot be changed, for example while a change is saved. */
  readonly disabled: boolean;
  /** Whether the requirement asks for an activity (T-184); a hint only, it never blocks. */
  readonly requireActivity: boolean;
  /** Called with the chosen activity, or `null` for „Ohne Tätigkeit“. */
  readonly onChange: (activityId: string | null) => void;
}

/**
 * Selection of the activity of project time (T-184, spec 6.7). „Ohne Tätigkeit“ is always possible:
 * a missing activity never blocks clocking or saving (`PINV-P07`); a requirement that asks for one
 * shows a hint only. An activity that is no longer choosable stays shown as such.
 *
 * @param props - Component properties.
 * @returns The field.
 */
export function ActivityChoice(props: ActivityChoiceProps): JSX.Element {
  const { t } = useI18n();
  const id = useId();
  const hintId = useId();
  const showsHint = props.requireActivity && props.value === null;
  const unknown =
    props.value === null || props.activities.some((activity) => activity.id === props.value)
      ? null
      : props.value;

  const field = (
    <div className={styles["field"]}>
      <label htmlFor={id}>{props.label}</label>
      <select
        id={id}
        value={props.value ?? ""}
        disabled={props.disabled}
        aria-describedby={showsHint ? hintId : undefined}
        onChange={(event) => {
          props.onChange(event.target.value === "" ? null : event.target.value);
        }}
      >
        <option value="">{t("activity.none")}</option>
        {props.activities.map((activity) => (
          <option key={activity.id} value={activity.id}>
            {activityLabel(activity)}
          </option>
        ))}
        {unknown === null ? null : <option value={unknown}>{t("activity.notChoosable")}</option>}
      </select>
      {showsHint ? (
        <p id={hintId} className={styles["muted"]}>
          {t("activity.requiredHint")}
        </p>
      ) : null}
    </div>
  );

  return field;
}
